import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { Gauge } from "lucide-react";
import React from "react";

export const ReadCycleProfileCard: React.FC<any> = ({ data }) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case "SCHEDULED":
        return (
          <Badge className={getStatusBadgeClass("SCHEDULED")}>Scheduled</Badge>
        );
      case "UNSCHEDULED":
        return (
          <Badge className={getStatusBadgeClass("UNSCHEDULED")}>
            Unscheduled
          </Badge>
        );
      default:
        return <Badge variant="outline">NA</Badge>;
    }
  };
  const productCodes =
    Array.isArray(data?.productCode) && data.productCode.length > 0
      ? data.productCode.join(", ")
      : "NA";

  const meterCycleDetails = [
    { label: "Read Cycle Name", value: data?.meterCycleName || "NA", toolTip: "The unique identifier and name of this meter reading cycle" },
    { label: "Utility Service", value: productCodes || "NA", toolTip: "The type of utility service being measured (e.g., Water, Gas, Electricity)" },
    { label: "Meter Type", value: data?.meterTypeDisplay || "NA", toolTip: "The method used for reading meters (e.g., Manual, Automated)" },
    { label: "Cycle Duration (days)", value: data?.cycleDuration || "NA", toolTip: "The total number of days allocated for completing this reading cycle" },
    { label: "Frequency", value: data?.frequencyDisplay || "NA", toolTip: "How often this cycle repeats (e.g., Monthly, Quarterly)" },
    { label: "Status", value: getStatusClass(data?.status) || "NA", toolTip: "Current operational status of the reading cycle" },
    { label: "Start Date", value: data?.startDate || "NA", toolTip: "The date when this reading cycle begins" },
    { label: "End Date", value: data?.endDate || "NA", toolTip: "The date when this reading cycle ends" },
    {
      label: "Current Reading Period",
      value: data?.startDate + " - " + data?.endDate || "NA",
      toolTip: "The active date range for current meter readings"
    },
  ];
  const runHistoryDetails = [
    { label: "Next Run Date", value: data?.nextRunDate || "NA", toolTip: "The scheduled date for the next reading cycle execution" },
    { label: "Previous Run Date", value: data?.lastRunDate || "NA", toolTip: "The date when the previous reading cycle was executed" },
    { label: "Created By", value: data?.createdUserRemoteName || "NA", toolTip: "The user who created this reading cycle" },
    { label: "Created On", value: data?.createdDate || "NA", toolTip: "The date when this reading cycle was created" },
    {
      label: "Last Updated By",
      value: data?.lastModifiedUserRemoteName || "NA",
      toolTip: "The user who last modified this reading cycle"
    },
    { label: "Last Updated On", value: data?.lastModifiedDate || "NA", toolTip: "The date when this reading cycle was last modified" },
  ];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg font-semibold">
              {data?.meterCycleName}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {getStatusClass(data?.status)}
          </div>
        </div>
      </CardHeader>
      <Card className="mt-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Read Cycle Overview
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-4 mt-3">
          {meterCycleDetails.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
              tooltip={item.toolTip}
            />
          ))}
        </div>
      </Card>
      <Card className="mt-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Run History & Administration
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-4 mt-3">
          {runHistoryDetails.map((item) => (
            <ValueLabel
              key={item.label}
              label={item.label}
              value={item.value}
              tooltip={item.toolTip}
            />
          ))}
        </div>
      </Card>
    </Card>
  );
};
